/* eslint-disable @typescript-eslint/no-misused-promises */
import { yupResolver } from '@hookform/resolvers/yup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Theme,
  ThemeOptions,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { Copy, ExternalLink, Plus, Trash2, Upload } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { ChangeEvent, FC, memo, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import {
  Control,
  Controller,
  FieldArrayPath,
  FormState,
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch
} from 'react-hook-form';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import LicenseContext from 'src/contexts/LicenseContext';
import { CompanyEntity } from 'src/entities/admin/companyEntity';
import { AreaEntity } from 'src/entities/areaEntity';
import { AreaSetting, ContractEntity, ContractRequestEntity, DistanceWeightSetting, PricingSetting } from 'src/entities/contractEntity';
import { useAreasRequest } from 'src/hooks/useAreas.request';
import { queryKey } from 'src/hooks/useContracts.request';
import { useMutationContract } from 'src/hooks/useQueryContracts';

import { schema } from './schema';

type Props = {
  dialogIsOpen: boolean;
  toggleDialogIsOpen: () => void;
  setDialogIsOpen: (open: boolean) => void;
  entity?: ContractEntity;
};

type FormPricingSetting = Omit<PricingSetting, 'vehicle_types' | 'distance_weight_settings'> & {
  vehicle_types: string[];
  distance_weight_settings: DistanceWeightSetting[];
};

// フォーム内部でのファイル管理構造
type FormDocument = {
  type: 'existing' | 'new';
  id: string;  // 一意識別子（既存: URL、新規: File.name + timestamp）
  filename: string;
  url?: string;  // 既存ファイルのみ
  file?: File;   // 新規ファイルのみ
};

type FormValues = Omit<ContractRequestEntity, 'pricing_settings' | 'document_urls' | 'documents'> & {
  id?: number;
  pricing_settings: FormPricingSetting[];
  documents: FormDocument[];  // 統一されたファイル管理
  deleted_document_urls?: string[];  // 削除対象URLのリスト
  // 互換性維持のため残すが、内部的にはdocumentsを使用
  document_urls?: string[];
  delete_documents?: boolean;
};

/** 距離×重量別設定にidが無い場合に一時idを付与（Excel取込等でidが保存されていない既存データ用） */
const ensureDistanceWeightSettingIds = (settings: DistanceWeightSetting[]): DistanceWeightSetting[] =>
  settings.map((dw, index) => {
    const numId = typeof dw.id === 'number' ? dw.id : index + 1;
    return { ...dw, id: numId };
  });

const toFormPricingSetting = (setting: PricingSetting): FormPricingSetting => ({
  ...setting,
  vehicle_types: setting.vehicle_types ?? [],
  distance_weight_settings: Array.isArray(setting.distance_weight_settings)
    ? ensureDistanceWeightSettingIds(setting.distance_weight_settings)
    : [],
  temperature_types: ['ambient'],
});

const dayOptions = Array.from({ length: 28 }, (_, i) => (i + 1).toString()).concat('末日');
const monthOptions = ['当月', '翌月', '翌々月', '3か月後', '4か月後', '5か月後', '6か月後'];
const areaTypeOptions = ['エリア', '都道府県', 'ユーザー指定エリア'];
const regionOptions = [
  '北海道', '東北', '関東', '甲信越', '北陸',
  '東海', '関西', '中国', '四国', '九州・沖縄'
];
const prefectureOptions = [
  '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
  '岐阜県', '静岡県', '愛知県', '三重県',
  '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
  '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県',
  '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
];
const vehicleTypeOptions = ['軽貨物', '1t', '2t', '4t', '10t', '20t'];

const createDefaultAreaSetting = (): AreaSetting => ({
  area_type: 'エリア',
  loading_area: '関東',
  unloading_area: '関東'
});

const createDefaultDistanceWeightSetting = (id: number): DistanceWeightSetting => ({
  id,
  distance_min: null,
  distance_max: null,
  weight_min: null,
  weight_max: null,
  price: null,
});

const createDefaultPricingSetting = (id: number): FormPricingSetting => ({
  id,
  pricing_method: '個建て',
  base_amount: 0,
  excess_rate: null,
  excess_threshold: null,
  minimum_order: null,
  minimum_order_fee: null,
  vehicle_types: [],
  distance_weight_settings: [],
  area_settings: [createDefaultAreaSetting()],
  temperature_types: ['ambient'],
});

type PricingSettingErrors = {
  distance_weight_settings?: Array<{
    distance_max?: { message?: string };
    weight_max?: { message?: string };
    price?: { message?: string };
  }>;
};

type DistanceWeightSettingsListProps = {
  pricingIndex: number;
  control: Control<FormValues>;
  pricingErrors?: PricingSettingErrors;
  distanceWeightSettingsErrorMessage?: string;
};

const DistanceWeightSettingsList: FC<DistanceWeightSettingsListProps> = memo(({
  pricingIndex,
  control,
  pricingErrors,
  distanceWeightSettingsErrorMessage,
}) => {
  const fieldArrayName = `pricing_settings.${pricingIndex}.distance_weight_settings` as FieldArrayPath<FormValues>;
  const { fields, append, remove, insert } = useFieldArray<FormValues>({
    control,
    name: fieldArrayName,
  });
  const watchedSettings = useWatch({ control, name: fieldArrayName }) as DistanceWeightSetting[] | undefined;
  const settingsList = watchedSettings ?? [];

  const handleAdd = () => {
    const nextId = Math.max(0, ...settingsList.map((s) => (s?.id != null ? Number(s.id) : 0))) + 1;
    append(createDefaultDistanceWeightSetting(nextId));
  };

  const handleInsertBelow = (settingIndex: number) => {
    const nextId = Math.max(0, ...settingsList.map((s) => (s?.id != null ? Number(s.id) : 0))) + 1;
    insert(settingIndex + 1, createDefaultDistanceWeightSetting(nextId));
  };

  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" fontWeight="bold">距離×重量別料金表</Typography>
        <IconButton size="small" onClick={handleAdd} title="追加">
          <Plus size={16} />
        </IconButton>
      </Box>

      {fields.map((field, settingIndex) => {
        const displayId = settingsList[settingIndex]?.id;
        return (
          <Paper key={field.id} variant="outlined" sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2">料金設定 {displayId}</Typography>
              <Box sx={{ display: 'flex', gap: 0 }}>
                <IconButton
                  size="small"
                  onClick={() => handleInsertBelow(settingIndex)}
                  title="直下に追加"
                >
                  <Plus size={16} />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => remove(settingIndex)}
                  title="削除"
                >
                  <Trash2 size={16} />
                </IconButton>
              </Box>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
              <Controller
                control={control}
                name={`pricing_settings.${pricingIndex}.distance_weight_settings.${settingIndex}.distance_min` as const}
                render={({ field: f }) => (
                  <TextField
                    {...f}
                    value={f.value ?? ''}
                    label="距離(最小)"
                    type="number"
                    fullWidth
                    variant="standard"
                    slotProps={{
                      input: { endAdornment: <InputAdornment position="end">km</InputAdornment> }
                    }}
                    onChange={(event) => f.onChange(event.target.value === '' ? null : Number(event.target.value))}
                  />
                )}
              />
              <Typography variant="body2" sx={{ pt: 2, }}>〜</Typography>
              <Controller
                control={control}
                name={`pricing_settings.${pricingIndex}.distance_weight_settings.${settingIndex}.distance_max` as const}
                render={({ field: f }) => (
                  <TextField
                    {...f}
                    value={f.value ?? ''}
                    label="距離(最大)"
                    type="number"
                    fullWidth
                    variant="standard"
                    slotProps={{
                      input: { endAdornment: <InputAdornment position="end">km</InputAdornment> }
                    }}
                    error={Boolean(pricingErrors?.distance_weight_settings?.[settingIndex]?.distance_max)}
                    helperText={pricingErrors?.distance_weight_settings?.[settingIndex]?.distance_max?.message}
                    onChange={(event) => f.onChange(event.target.value === '' ? null : Number(event.target.value))}
                  />
                )}
              />
              <Controller
                control={control}
                name={`pricing_settings.${pricingIndex}.distance_weight_settings.${settingIndex}.weight_min` as const}
                render={({ field: f }) => (
                  <TextField
                    {...f}
                    value={f.value ?? ''}
                    label="重量(最小)"
                    type="number"
                    fullWidth
                    variant="standard"
                    slotProps={{
                      input: { endAdornment: <InputAdornment position="end">kg</InputAdornment> }
                    }}
                    onChange={(event) => f.onChange(event.target.value === '' ? null : Number(event.target.value))}
                  />
                )}
              />
              <Typography variant="body2" sx={{ pt: 2, }}>〜</Typography>
              <Controller
                control={control}
                name={`pricing_settings.${pricingIndex}.distance_weight_settings.${settingIndex}.weight_max` as const}
                render={({ field: f }) => (
                  <TextField
                    {...f}
                    value={f.value ?? ''}
                    label="重量(最大)"
                    type="number"
                    fullWidth
                    variant="standard"
                    slotProps={{
                      input: { endAdornment: <InputAdornment position="end">kg</InputAdornment> }
                    }}
                    error={Boolean(pricingErrors?.distance_weight_settings?.[settingIndex]?.weight_max)}
                    helperText={pricingErrors?.distance_weight_settings?.[settingIndex]?.weight_max?.message}
                    onChange={(event) => f.onChange(event.target.value === '' ? null : Number(event.target.value))}
                  />
                )}
              />
              <Controller
                control={control}
                name={`pricing_settings.${pricingIndex}.distance_weight_settings.${settingIndex}.price` as const}
                render={({ field: f }) => (
                  <TextField
                    {...f}
                    value={f.value ?? ''}
                    label="料金"
                    type="number"
                    fullWidth
                    variant="standard"
                    slotProps={{
                      input: { endAdornment: <InputAdornment position="end">円</InputAdornment> }
                    }}
                    error={Boolean(pricingErrors?.distance_weight_settings?.[settingIndex]?.price)}
                    helperText={pricingErrors?.distance_weight_settings?.[settingIndex]?.price?.message}
                    onChange={(event) => f.onChange(event.target.value === '' ? null : Number(event.target.value))}
                  />
                )}
              />
            </Stack>
          </Paper>
        );
      })}

      {typeof distanceWeightSettingsErrorMessage === 'string' && (
        <FormHelperText error>{distanceWeightSettingsErrorMessage}</FormHelperText>
      )}
    </Stack>
  );
});

DistanceWeightSettingsList.displayName = 'DistanceWeightSettingsList';

type SinglePricingErrors = FormState<FormValues>['errors'] extends { pricing_settings?: readonly (infer E)[] } ? E : Record<string, unknown>;

type PricingSettingBlockProps = {
  control: Control<FormValues>;
  setValue: ReturnType<typeof useForm<FormValues>>['setValue'];
  index: number;
  fieldItem: { id: string };
  pricingErrors: SinglePricingErrors | undefined;
  fieldsCount: number;
  onRemove: () => void;
  onCopy: () => void;
  onAddBelow: () => void;
  getAreaOptions: (areaType: string) => string[];
};

const PricingSettingBlock: FC<PricingSettingBlockProps> = memo(({
  control,
  setValue,
  index,
  fieldItem: _fieldItem,
  pricingErrors,
  fieldsCount,
  onRemove,
  onCopy,
  onAddBelow,
  getAreaOptions,
}) => {
  const watchedSetting = useWatch({
    control,
    name: `pricing_settings.${index}` as FieldArrayPath<FormValues>,
  }) as unknown as FormPricingSetting | undefined;
  const setting = watchedSetting ?? undefined;
  const method = setting?.pricing_method;
  const currentAreas = setting?.area_settings ?? [];
  const currentVehicles = setting?.vehicle_types ?? [];
  const distanceWeightSettingsErrorMessage = (pricingErrors?.distance_weight_settings as { message?: unknown } | undefined)?.message;

  const handleAddAreaSetting = () => {
    setValue(
      `pricing_settings.${index}.area_settings`,
      [...currentAreas, createDefaultAreaSetting()],
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const handleRemoveAreaSetting = (areaIndex: number) => {
    const nextAreas = currentAreas.filter((_, idx) => idx !== areaIndex);
    setValue(`pricing_settings.${index}.area_settings`, nextAreas, { shouldDirty: true, shouldValidate: true });
  };

  const handleInsertAreaSetting = (areaIndex: number) => {
    const nextAreas = [
      ...currentAreas.slice(0, areaIndex + 1),
      createDefaultAreaSetting(),
      ...currentAreas.slice(areaIndex + 1),
    ];
    setValue(`pricing_settings.${index}.area_settings`, nextAreas, { shouldDirty: true, shouldValidate: true });
  };

  const handleAreaTypeChange = (areaIndex: number, newAreaType: string) => {
    const newOptions = getAreaOptions(newAreaType);
    const defaultValue = newOptions[0] || '';
    setValue(`pricing_settings.${index}.area_settings.${areaIndex}.area_type`, newAreaType, { shouldDirty: true, shouldValidate: true });
    setValue(`pricing_settings.${index}.area_settings.${areaIndex}.loading_area`, defaultValue, { shouldDirty: true, shouldValidate: true });
    setValue(`pricing_settings.${index}.area_settings.${areaIndex}.unloading_area`, defaultValue, { shouldDirty: true, shouldValidate: true });
  };

  const handleAddVehicleType = (value: string) => {
    if (!value) return;
    if (currentVehicles.includes(value)) return;
    setValue(`pricing_settings.${index}.vehicle_types`, [...currentVehicles, value], { shouldDirty: true, shouldValidate: true });
  };

  const handleRemoveVehicleType = (vehicleIndex: number) => {
    const nextVehicles = currentVehicles.filter((_, idx) => idx !== vehicleIndex);
    setValue(`pricing_settings.${index}.vehicle_types`, nextVehicles, { shouldDirty: true, shouldValidate: true });
  };

  const handleChangePricingMethod = (nextMethod: PricingSetting['pricing_method']) => {
    setValue(`pricing_settings.${index}.pricing_method`, nextMethod, { shouldDirty: true, shouldValidate: true });
    if (nextMethod === 'トンキロ建て') {
      setValue(`pricing_settings.${index}.base_amount`, null, { shouldDirty: true, shouldValidate: true });
      const currentSettings = setting?.distance_weight_settings ?? [];
      if (currentSettings.length === 0) {
        setValue(
          `pricing_settings.${index}.distance_weight_settings`,
          [createDefaultDistanceWeightSetting(1)],
          { shouldDirty: true, shouldValidate: true }
        );
      }
      return;
    }
    setValue(`pricing_settings.${index}.distance_weight_settings`, [], { shouldDirty: true, shouldValidate: true });
  };

  const renderAreaSettings = () => (
    <Stack spacing={1.5}>
      {currentAreas.map((areaSetting, areaIndex) => {
        const currentAreaType = areaSetting?.area_type ?? 'エリア';
        const availableOptions = getAreaOptions(currentAreaType);
        return (
          <Paper key={`area-${index}-${areaIndex}`} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems="flex-start">
              <FormControl fullWidth size="small">
                <InputLabel>エリア種別</InputLabel>
                <Controller
                  control={control}
                  name={`pricing_settings.${index}.area_settings.${areaIndex}.area_type` as const}
                  render={({ field }) => (
                    <Select
                      {...field}
                      size="small"
                      label="エリア種別"
                      onChange={(e) => handleAreaTypeChange(areaIndex, e.target.value)}
                    >
                      {areaTypeOptions.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText error>{(pricingErrors?.area_settings as { [i: number]: { area_type?: { message?: string } } } | undefined)?.[areaIndex]?.area_type?.message}</FormHelperText>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>積地</InputLabel>
                <Controller
                  control={control}
                  name={`pricing_settings.${index}.area_settings.${areaIndex}.loading_area` as const}
                  render={({ field }) => (
                    <Select {...field} size="small" label="積地">
                      {availableOptions.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText error>{(pricingErrors?.area_settings as { [i: number]: { loading_area?: { message?: string } } } | undefined)?.[areaIndex]?.loading_area?.message}</FormHelperText>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>降地</InputLabel>
                <Controller
                  control={control}
                  name={`pricing_settings.${index}.area_settings.${areaIndex}.unloading_area` as const}
                  render={({ field }) => (
                    <Select {...field} size="small" label="降地">
                      {availableOptions.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText error>{(pricingErrors?.area_settings as { [i: number]: { unloading_area?: { message?: string } } } | undefined)?.[areaIndex]?.unloading_area?.message}</FormHelperText>
              </FormControl>
              <IconButton
                size="small"
                onClick={() => handleInsertAreaSetting(areaIndex)}
                title="直下に追加"
                sx={{ mt: { xs: 1, sm: 0.5 } }}
              >
                <Plus size={16} />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleRemoveAreaSetting(areaIndex)}
                sx={{ mt: { xs: 1, sm: 0.5 } }}
              >
                <Trash2 size={16} />
              </IconButton>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" fontWeight="bold">
          料金設定 {index + 1}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={onCopy} title="複製">
            <Copy size={16} />
          </IconButton>
          <IconButton size="small" onClick={onAddBelow} title="直下に追加">
            <Plus size={16} />
          </IconButton>
          {fieldsCount > 1 && (
            <IconButton size="small" color="error" onClick={onRemove} title="削除">
              <Trash2 size={16} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
          <FormControl fullWidth variant="standard" error={Boolean(pricingErrors?.pricing_method)}>
            <InputLabel id={`pricing_method_${index}`}>課金方式 *</InputLabel>
            <Controller
              control={control}
              name={`pricing_settings.${index}.pricing_method` as const}
              render={({ field }) => (
                <>
                  <Select
                    labelId={`pricing_method_${index}`}
                    label="課金方式"
                    value={field.value}
                    onChange={(event: SelectChangeEvent<PricingSetting['pricing_method']>) =>
                      handleChangePricingMethod(event.target.value)
                    }
                  >
                    <MenuItem value="個建て">個建て</MenuItem>
                    <MenuItem value="車建て">車建て</MenuItem>
                    <MenuItem value="トンキロ建て">トンキロ建て</MenuItem>
                  </Select>
                  <FormHelperText>{(pricingErrors?.pricing_method as { message?: string })?.message}</FormHelperText>
                </>
              )}
            />
          </FormControl>
        </Stack>

        {method === '個建て' && (
          <Controller
            control={control}
            name={`pricing_settings.${index}.base_amount` as const}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="1個あたり基本料金"
                type="number"
                fullWidth
                variant="standard"
                error={Boolean(pricingErrors?.base_amount)}
                helperText={(pricingErrors?.base_amount as { message?: string })?.message}
                slotProps={{ input: { endAdornment: <InputAdornment position="end">円</InputAdornment> } }}
                onChange={(event) => field.onChange(event.target.value === '' ? '' : Number(event.target.value))}
              />
            )}
          />
        )}
        {method === '車建て' && (
          <Controller
            control={control}
            name={`pricing_settings.${index}.base_amount` as const}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="基本料金"
                type="number"
                fullWidth
                variant="standard"
                error={Boolean(pricingErrors?.base_amount)}
                helperText={(pricingErrors?.base_amount as { message?: string })?.message}
                slotProps={{ input: { endAdornment: <InputAdornment position="end">円/台</InputAdornment> } }}
                onChange={(event) => field.onChange(event.target.value === '' ? '' : Number(event.target.value))}
              />
            )}
          />
        )}
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Accordion
          disableGutters
          elevation={0}
          defaultExpanded
          sx={{ p: 0, m: 0, bgcolor: 'transparent', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0, m: 0, pt: 0 }}>
            <Typography variant="subtitle1">詳細</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, m: 0, pt: 0 }}>
            <Stack spacing={2}>
              {method === '個建て' && (
                <Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                    <Controller
                      control={control}
                      name={`pricing_settings.${index}.excess_threshold` as const}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ''}
                          margin="dense"
                          label="超過料金閾値"
                          type="number"
                          fullWidth
                          variant="standard"
                          error={Boolean(pricingErrors?.excess_threshold)}
                          helperText={(pricingErrors?.excess_threshold as { message?: string })?.message ?? '個数'}
                          onChange={(event) => field.onChange(event.target.value === '' ? null : Number(event.target.value))}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name={`pricing_settings.${index}.excess_rate` as const}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ''}
                          margin="dense"
                          label="超過料金単価"
                          type="number"
                          fullWidth
                          variant="standard"
                          error={Boolean(pricingErrors?.excess_rate)}
                          helperText={(pricingErrors?.excess_rate as { message?: string })?.message}
                          slotProps={{ input: { endAdornment: <InputAdornment position="end">円/個</InputAdornment> } }}
                          onChange={(event) => field.onChange(event.target.value === '' ? null : Number(event.target.value))}
                        />
                      )}
                    />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                    <Controller
                      control={control}
                      name={`pricing_settings.${index}.minimum_order` as const}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ''}
                          margin="dense"
                          label="最低発注量"
                          type="number"
                          fullWidth
                          variant="standard"
                          error={Boolean(pricingErrors?.minimum_order)}
                          helperText={(pricingErrors?.minimum_order as { message?: string })?.message ?? '個数'}
                          onChange={(event) => field.onChange(event.target.value === '' ? null : Number(event.target.value))}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name={`pricing_settings.${index}.minimum_order_fee` as const}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ''}
                          margin="dense"
                          label="最低発注量割れ時の料金"
                          type="number"
                          fullWidth
                          variant="standard"
                          error={Boolean(pricingErrors?.minimum_order_fee)}
                          helperText={(pricingErrors?.minimum_order_fee as { message?: string })?.message ?? '最低発注量を下回った場合の最低料金'}
                          slotProps={{ input: { endAdornment: <InputAdornment position="end">円</InputAdornment> } }}
                          onChange={(event) => field.onChange(event.target.value === '' ? null : Number(event.target.value))}
                        />
                      )}
                    />
                  </Stack>
                </Stack>
              )}
              {method === '車建て' && (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>対象車両タイプ</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      {currentVehicles.map((type, vehicleIndex) => (
                        <Chip
                          key={`${type}-${vehicleIndex}`}
                          label={type}
                          onDelete={() => handleRemoveVehicleType(vehicleIndex)}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 160 }} error={Boolean(pricingErrors?.vehicle_types)}>
                      <InputLabel id={`vehicle_type_add_${index}`}>車両タイプ追加</InputLabel>
                      <Select
                        labelId={`vehicle_type_add_${index}`}
                        label="車両タイプ追加"
                        value=""
                        onChange={(event) => handleAddVehicleType(event.target.value as string)}
                      >
                        {vehicleTypeOptions
                          .filter((option) => !currentVehicles.includes(option))
                          .map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                      </Select>
                      <FormHelperText>{(pricingErrors?.vehicle_types as { message?: string })?.message}</FormHelperText>
                    </FormControl>
                  </Box>
                </Stack>
              )}
              {method === 'トンキロ建て' && (
                <DistanceWeightSettingsList
                  pricingIndex={index}
                  control={control}
                  pricingErrors={pricingErrors as PricingSettingErrors | undefined}
                  distanceWeightSettingsErrorMessage={typeof distanceWeightSettingsErrorMessage === 'string' ? distanceWeightSettingsErrorMessage : undefined}
                />
              )}
              <Divider />
              <Typography variant="h5" fontWeight="bold">適用条件</Typography>
              <Box>
                <Stack direction="row" justifyContent="space-between" justifyItems="flex-end" sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }} fontWeight="bold">対象エリア</Typography>
                  <IconButton size="small" onClick={handleAddAreaSetting}>
                    <Plus size={16} />
                  </IconButton>
                </Stack>
                {renderAreaSettings()}
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Paper>
  );
});

PricingSettingBlock.displayName = 'PricingSettingBlock';

type ContractDocumentsBlockProps = {
  control: Control<FormValues>;
  onAddClick: () => void;
  onRemove: (docId: string) => void;
  children?: ReactNode;
};

const ContractDocumentsBlock: FC<ContractDocumentsBlockProps> = memo(({ control, onAddClick, onRemove, children }) => {
  const documents = useWatch({ control, name: 'documents' }) ?? [];
  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 1.5, fontWeight: 'bold' }}>
        契約書
      </Typography>
      <Stack spacing={2}>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Upload size={18} />}
            onClick={onAddClick}
          >
            契約書を追加アップロード
          </Button>
          {children}
        </Box>
        {documents.length > 0 ? (
          <Stack spacing={1}>
            <Typography variant="body2" fontWeight="bold">
              アップロード済みファイル ({documents.length}件)
            </Typography>
            {documents.map((doc: FormDocument) => (
              <Paper
                key={doc.id}
                variant="outlined"
                sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {doc.filename}
                  </Typography>
                  {doc.type === 'new' && (
                    <Chip label="新規" size="small" color="success" variant="outlined" />
                  )}
                  {doc.type === 'existing' && doc.url && (
                    <IconButton
                      size="small"
                      component="a"
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="プレビュー"
                    >
                      <ExternalLink size={16} />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onRemove(doc.id)}
                    title="削除"
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            PDF、Word、Excel形式のファイルに対応しています（最大10MB）
          </Typography>
        )}
      </Stack>
    </Paper>
  );
});

ContractDocumentsBlock.displayName = 'ContractDocumentsBlock';

type PaymentBillingBlockProps = {
  control: Control<FormValues>;
  errors: FormState<FormValues>['errors'];
};

const PaymentBillingBlock: FC<PaymentBillingBlockProps> = memo(({ control, errors }) => {
  const contractType = useWatch({ control, name: 'contract_type' });
  const title = contractType === 'billing' ? '請求設定' : '支払設定';
  const paymentMonthLabel = contractType === 'billing' ? '入金月' : '支払月';
  const paymentDayLabel = contractType === 'billing' ? '入金日' : '支払日';
  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
        {title}
      </Typography>
      <Stack spacing={2}>
        <FormControl fullWidth variant="standard" error={'closing_day' in errors}>
          <InputLabel id="closing_day_label">締め日</InputLabel>
          <Controller
            control={control}
            name="closing_day"
            render={({ field }) => (
              <>
                <Select labelId="closing_day_label" label="締め日" {...field}>
                  {dayOptions.map((day) => (
                    <MenuItem key={day} value={day}>{day === '末日' ? day : `${day}日`}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.closing_day?.message}</FormHelperText>
              </>
            )}
          />
        </FormControl>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
          <FormControl fullWidth variant="standard" error={'payment_month' in errors}>
            <InputLabel id="payment_month_label">{paymentMonthLabel}</InputLabel>
            <Controller
              control={control}
              name="payment_month"
              render={({ field }) => (
                <>
                  <Select labelId="payment_month_label" label={paymentMonthLabel} {...field}>
                    {monthOptions.map((month) => (
                      <MenuItem key={month} value={month}>{month}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.payment_month?.message}</FormHelperText>
                </>
              )}
            />
          </FormControl>
          <FormControl fullWidth variant="standard" error={'payment_day' in errors}>
            <InputLabel id="payment_day_label">{paymentDayLabel}</InputLabel>
            <Controller
              control={control}
              name="payment_day"
              render={({ field }) => (
                <>
                  <Select labelId="payment_day_label" label={paymentDayLabel} {...field}>
                    {dayOptions.map((day) => (
                      <MenuItem key={day} value={day}>{day === '末日' ? day : `${day}日`}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.payment_day?.message}</FormHelperText>
                </>
              )}
            />
          </FormControl>
        </Stack>
      </Stack>
    </Paper>
  );
});

PaymentBillingBlock.displayName = 'PaymentBillingBlock';

const FormDialog: FC<Props> = memo(({
  dialogIsOpen,
  toggleDialogIsOpen,
  setDialogIsOpen,
  entity,
}) => {
  const licenseContext = useContext(LicenseContext);
  const queryClient = useQueryClient();
  const theme: Theme & ThemeOptions = useTheme();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    control,
    reset,
    register,
    handleSubmit,
    setValue,
    getValues,
    formState,
  } = useForm<FormValues>({
    resolver: yupResolver(schema)
  });

  const { errors } = formState;

  const onCloseDialog = () => {
    if (formState.isDirty) {
      setConfirmDialogOpen(true);
      return;
    }
    toggleDialogIsOpen();
  }

  const { fields, append, remove, insert } = useFieldArray<FormValues, 'pricing_settings'>({
    control,
    name: 'pricing_settings'
  });

  const { addContract, updateContract } = useMutationContract();
  const [loading, setLoading] = useState<boolean>(false);
  const [companyId, setCompanyId] = useState<number>(licenseContext?.config?.selected_company_id ?? 0);
  const [selectableCompanies, setSelectableCompanies] = useState<CompanyEntity[]>([]);
  const [editEntity, setEditEntity] = useState<ContractEntity | undefined>(undefined);
  const { enqueueSnackbar } = useSnackbar();

  // ユーザー指定エリアの取得
  const { data: areasData } = useAreasRequest(0, '', '', Number.MAX_SAFE_INTEGER);
  const userAreas: AreaEntity[] = areasData?.data ?? [];
  const userAreaOptions = userAreas.map((area) => area.name);


  // エリア種別に応じた選択肢を取得する関数
  const getAreaOptions = (areaType: string): string[] => {
    switch (areaType) {
      case 'エリア':
        return regionOptions;
      case '都道府県':
        return prefectureOptions;
      case 'ユーザー指定エリア':
        return userAreaOptions;
      default:
        return regionOptions;
    }
  };

  const onSubmitForm: SubmitHandler<FormValues> = async (data: FormValues): Promise<void> => {
    setLoading(true);

    try {
      data.company_id = companyId;

      // トンキロ建ての料金設定は base_amount を送らない（null に正規化）
      const normalizedPricingSettings = (data.pricing_settings ?? []).map((setting) =>
        setting.pricing_method === 'トンキロ建て' ? { ...setting, base_amount: null } : setting
      );
      data.pricing_settings = normalizedPricingSettings;

      // FormDataを作成してファイルとデータを送信
      const formData = new FormData();

      // 契約データをFormDataに追加（documents、deleted_document_urlsを除く）
      Object.entries(data).forEach(([key, value]) => {
        if (key === 'pricing_settings') {
          formData.append(key, JSON.stringify(value));
        } else if (key === 'id' && typeof value === 'number') {
          formData.append(key, String(value));
        } else if (key === 'documents' || key === 'deleted_document_urls' || key === 'document_urls' || key === 'delete_documents') {
          // 後で個別処理
        } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          formData.append(key, String(value));
        }
      });

      // 新規ファイルをFormDataに追加
      const newFiles = (data.documents ?? [])
        .filter((doc): doc is FormDocument & { file: File } => doc.type === 'new' && Boolean(doc.file));

      if (newFiles.length > 0) {
        newFiles.forEach((doc) => {
          formData.append('documents[]', doc.file);
        });
      }

      // 削除対象URLをFormDataに追加
      const deletedUrls = data.deleted_document_urls ?? [];
      if (deletedUrls.length > 0) {
        deletedUrls.forEach((url) => {
          formData.append('deleted_document_urls[]', url);
        });
      }

      // has_documentの計算
      const remainingExisting = (data.documents ?? []).filter((doc) => doc.type === 'existing');
      data.has_document = remainingExisting.length > 0 || newFiles.length > 0;
      formData.set('has_document', String(data.has_document));

      if (typeof data.id === 'number') {
        await updateContract.mutateAsync(formData as unknown as ContractEntity);
        enqueueSnackbar('契約情報を更新しました');
      } else {
        await addContract.mutateAsync(formData as unknown as ContractRequestEntity);
        enqueueSnackbar('契約を追加しました');
      }

      await queryClient.invalidateQueries({ queryKey: [queryKey] });
      setDialogIsOpen(false);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response) {
        console.log(e.response.data);

        const axiosError = e as AxiosError<{ errors: string[] }>;
        const errorMessage = axiosError.response.data.errors.join(', ');
        enqueueSnackbar(`エラーが発生しました。${errorMessage}`, { variant: 'error' });
        return;
      }
      console.log(e);

      enqueueSnackbar('エラーが発生しました', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const onChangeCompany = (e: SelectChangeEvent) => {
    setValue('company_id', Number(e.target.value), { shouldDirty: true, shouldValidate: true });
    setCompanyId(Number(e.target.value));
  };

  const handleAddPricingSetting = () => {
    const current = getValues('pricing_settings') ?? [];
    const nextId = current.reduce((max: number, cur: FormPricingSetting | undefined) => Math.max(max, cur?.id ?? 0), 0) + 1;
    append(createDefaultPricingSetting(nextId));
  };

  const handleInsertPricingSetting = (atIndex: number) => {
    const current = getValues('pricing_settings') ?? [];
    const nextId = current.reduce((max: number, cur: FormPricingSetting | undefined) => Math.max(max, cur?.id ?? 0), 0) + 1;
    insert(atIndex + 1, createDefaultPricingSetting(nextId));
  };

  const handleCopyPricingSetting = (index: number) => {
    const current = getValues('pricing_settings') ?? [];
    const source = current[index];
    if (!source) return;
    const nextId = current.reduce((max: number, cur: FormPricingSetting | undefined) => Math.max(max, cur?.id ?? 0), 0) + 1;
    const copied: FormPricingSetting = {
      ...source,
      id: nextId,
      area_settings: source.area_settings?.map((area) => ({ ...area })) ?? [createDefaultAreaSetting()],
      temperature_types: [...(source.temperature_types ?? [])],
      vehicle_types: source.vehicle_types ? [...source.vehicle_types] : [],
      distance_weight_settings: source.distance_weight_settings.map((setting) => ({ ...setting })),
    };
    append(copied);
  };

  const handleRemovePricingSetting = (index: number) => {
    if (fields.length === 1) return;
    remove(index);
  };

  const handleDocumentButtonClick = () => {
    fileInputRef.current?.click();
  };

  // 新規ファイル追加ロジック（上書きではなく追加）
  const handleDocumentAdd = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    // ファイルサイズチェック（10MB）
    const maxSize = 10 * 1024 * 1024;
    const oversizedFiles = files.filter((file) => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      enqueueSnackbar(
        `ファイルサイズが10MBを超えています: ${oversizedFiles.map(f => f.name).join(', ')}`,
        { variant: 'error' }
      );
      return;
    }

    const currentDocs = getValues('documents') ?? [];
    const newDocs: FormDocument[] = files.map((file) => ({
      type: 'new',
      id: `${file.name}_${Date.now()}_${Math.random()}`,
      filename: file.name,
      file,
    }));

    // 既存に追加（上書きではない）
    setValue('documents', [...currentDocs, ...newDocs], { shouldDirty: true, shouldValidate: true });
    setValue('has_document', true, { shouldDirty: true, shouldValidate: true });

    // input要素をリセット（同じファイルを再選択可能に）
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 個別ファイル削除ロジック
  const handleDocumentRemove = (docId: string) => {
    const currentDocs = getValues('documents') ?? [];
    const targetDoc = currentDocs.find((doc) => doc.id === docId);

    if (!targetDoc) return;

    // リストから削除
    const nextDocs = currentDocs.filter((doc) => doc.id !== docId);
    setValue('documents', nextDocs, { shouldDirty: true, shouldValidate: true });

    // 既存ファイルの場合、削除URLリストに追加
    if (targetDoc.type === 'existing' && targetDoc.url) {
      const currentDeleted = getValues('deleted_document_urls') ?? [];
      setValue('deleted_document_urls', [...currentDeleted, targetDoc.url], { shouldDirty: true, shouldValidate: true });
    }

    // 全て削除された場合
    if (nextDocs.length === 0) {
      setValue('has_document', false, { shouldDirty: true, shouldValidate: true });
    }
  };

  const haveSelectableCompanies = licenseContext?.config?.selectable_companies?.length > 1;
  const isNew = !editEntity;

  useEffect(() => {
    setEditEntity(entity || null);
  }, [entity]);

  useEffect(() => {
    setSelectableCompanies(licenseContext.config?.selectable_companies ?? []);
  }, [licenseContext.config]);

  useEffect(() => {
    const formPricing = editEntity?.pricing_settings?.length
      ? editEntity.pricing_settings.map(toFormPricingSetting)
      : undefined;

    const initialValues: FormValues = editEntity ? {
      ...editEntity,
      pricing_settings: (formPricing && formPricing.length > 0) ? formPricing : [createDefaultPricingSetting(1)],
      documents: editEntity.documents?.length
        ? editEntity.documents.map((doc) => ({
            type: 'existing' as const,
            id: doc.url,
            filename: doc.filename,
            url: doc.url,
          }))
        : [],
      deleted_document_urls: [],
      has_document: editEntity.has_document ?? (editEntity.documents?.length ?? 0) > 0,
    } : {
      contract_type: 'billing',
      status: 'active',
      closing_day: '末日',
      payment_month: '翌月',
      payment_day: '末日',
      pricing_settings: [createDefaultPricingSetting(1)],
      conditions: '',
      has_document: false,
      documents: [],
      deleted_document_urls: [],
      start_date: new Date().toISOString().split('T')[0],
      company_id: licenseContext?.config?.selected_company_id ?? 0,
      contractor_name: '',
      applicable_contractor_names: '',
    };

    reset(initialValues);
    setCompanyId(editEntity?.company_id ?? licenseContext?.config?.selected_company_id ?? 0);
  }, [dialogIsOpen, editEntity, reset, licenseContext?.config]);

  const ready = () => {
    if (editEntity) {
      return !!editEntity;
    }
    return true;
  };

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  function handleConfirmOK(): void {
    setConfirmDialogOpen(false);
    toggleDialogIsOpen();
  }

  function handleConfirmCancel(): void {
    setConfirmDialogOpen(false);
  }

  if (!ready()) return null;
  if (!dialogIsOpen) return null;

  return (
    <Dialog open={dialogIsOpen} maxWidth="md" fullWidth onClose={onCloseDialog}>
      <DialogTitle>
        契約
        {isNew ? '追加' : '編集'}
      </DialogTitle>
      <DialogContent>
        <Stack component="form" noValidate spacing={2}>
          {/* 事業所選択 */}
          {[haveSelectableCompanies, isNew].every((bool) => bool) && (
            <FormControl
              fullWidth
              size="small"
              sx={{
                marginTop: theme.spacing(1),
              }}
            >
              <InputLabel id="company_id_label" sx={{ pt: 1, ml: -1.5 }}>事業所</InputLabel>
              <Controller
                control={control}
                name="company_id"
                render={() => (
                  <Select
                    id="company_id"
                    margin="dense"
                    label="事業所"
                    type="number"
                    fullWidth
                    variant="standard"
                    value={`${companyId}`}
                    onChange={onChangeCompany}
                    disabled={!haveSelectableCompanies}
                  >
                    {selectableCompanies.map((company) => (
                      <MenuItem value={company.id} key={company.id}>{company.name}</MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          )}

          <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
            {/* 基本情報 */}
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              基本情報
            </Typography>

            <Stack direction="row" gap={2} alignItems="center">
              <TextField
                required
                margin="dense"
                id="contractor_name"
                label="契約先名"
                type="text"
                fullWidth
                variant="standard"
                {...register('contractor_name')}
                error={'contractor_name' in errors}
                helperText={errors.contractor_name?.message}
              />

              <FormControl
                variant="standard"
                error={'contract_type' in errors}
                sx={{ minWidth: 160 }}
              >
                <InputLabel id="contract_type_label">契約区分 *</InputLabel>
                <Controller
                  control={control}
                  name="contract_type"
                  render={({ field }) => (
                    <>
                      <Select
                        labelId="contract_type_label"
                        id="contract_type"
                        label="契約区分"
                        {...field}
                      >
                        <MenuItem value="billing">請求</MenuItem>
                        <MenuItem value="payment">支払い</MenuItem>
                      </Select>
                      <FormHelperText>{errors.contract_type?.message}</FormHelperText>
                    </>
                  )}
                />
              </FormControl>
            </Stack>

            <TextField
              margin="dense"
              id="applicable_contractor_names"
              label="適用対象契約先名（カンマ区切り）"
              fullWidth
              variant="standard"
              multiline
              minRows={2}
              placeholder="例 : Logpose, ログポース, L-tech"
              {...register('applicable_contractor_names')}
              error={'applicable_contractor_names' in errors}
              helperText={
                (errors.applicable_contractor_names as { message?: string } | undefined)?.message
                  ?? 'カンマまたは改行で複数の契約先名を登録できます。表記揺れ等で複数の名称が出てくる場合に有効です。'
              }
            />

            <Stack direction="row" gap={2}>
              <TextField
                required
                margin="dense"
                id="start_date"
                label="契約開始日"
                type="date"
                fullWidth
                variant="standard"
                slotProps={{ inputLabel: { shrink: true }}}
                {...register('start_date')}
                error={'start_date' in errors}
                helperText={errors.start_date?.message || "デフォルトは今日の日付です"}
              />

              <TextField
                margin="dense"
                id="end_date"
                label="契約終了日"
                type="date"
                fullWidth
                variant="standard"
                slotProps={{ inputLabel: { shrink: true }}}
                {...register('end_date')}
                error={'end_date' in errors}
                helperText={errors.end_date?.message || "未定の場合は空欄で構いません"}
              />
            </Stack>

            <FormControl
              fullWidth
              variant="standard"
              error={'status' in errors}
            >
              <InputLabel id="status_label">ステータス *</InputLabel>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <>
                    <Select
                      labelId="status_label"
                      id="status"
                      label="ステータス"
                      {...field}
                    >
                      <MenuItem value="active">有効</MenuItem>
                      <MenuItem value="inactive">無効</MenuItem>
                    </Select>
                    <FormHelperText>{errors.status?.message}</FormHelperText>
                  </>
                )}
              />
            </FormControl>
          </Paper>

          <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
              {/* 料金設定 */}
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                料金設定
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="outlined" onClick={handleAddPricingSetting} startIcon={<Plus size={16} />} sx={{ px: 1, py: 0.5 }}>
                  料金設定を追加
                </Button>
              </Box>
            </Stack>

            <Stack spacing={2}>
              {fields.map((fieldItem, index) => (
                <PricingSettingBlock
                  key={fieldItem.id}
                  control={control}
                  setValue={setValue}
                  index={index}
                  fieldItem={fieldItem}
                  pricingErrors={errors.pricing_settings?.[index]}
                  fieldsCount={fields.length}
                  onRemove={() => handleRemovePricingSetting(index)}
                  onCopy={() => handleCopyPricingSetting(index)}
                  onAddBelow={() => handleInsertPricingSetting(index)}
                  getAreaOptions={getAreaOptions}
                />
              ))}
            </Stack>
          </Paper>

          {/* 備考 */}
          <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              備考
            </Typography>
            <TextField
              margin="dense"
              id="conditions"
              label="契約全体の備考"
              type="text"
              fullWidth
              multiline
              rows={3}
              variant="standard"
              {...register('conditions')}
              error={'conditions' in errors}
              helperText={errors.conditions?.message}
            />
          </Paper>

          <ContractDocumentsBlock
            control={control}
            onAddClick={handleDocumentButtonClick}
            onRemove={handleDocumentRemove}
          >
            <input
              type="file"
              hidden
              ref={fileInputRef}
              onChange={handleDocumentAdd}
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx"
            />
          </ContractDocumentsBlock>

          <PaymentBillingBlock control={control} errors={errors} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCloseDialog} disabled={loading}>キャンセル</Button>
        <Button onClick={handleSubmit(onSubmitForm)} variant="contained" disabled={loading}>
          保存する
        </Button>
      </DialogActions>
      <ConfirmDialog
        open={confirmDialogOpen}
        message="保存していない変更を破棄しますか？"
        handleOk={handleConfirmOK}
        handleCancel={handleConfirmCancel}
      />
    </Dialog>
  );
});

export default FormDialog;
